import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// CONFIGS
import { PaginationConfig, SnackBarConfig } from '../../../shared/configs';

// INTERFACES
import { IAppIntegrationAccount, IPagination } from '../../../shared/interfaces';

// SERVICES
import { AppIntegrationAccountLocationApiService } from '../../../shared/services/api/app-integration';

// UTILS
import { trackById } from '../../../shared/util';
import { IAppIntegrationLocation } from '../../intefaces/app-integration-locations.interface';

@Component({
  selector: 'lum-app-integration-account-location',
  templateUrl: './app-integration-account-location.component.html',
  styleUrls: ['./app-integration-account-location.component.scss'],
})
export class AppIntegrationAccountLocationComponent implements OnInit, OnDestroy {
  inventoryLocations :IAppIntegrationLocation[] = [];
  loading :boolean = false;
  btnGetLocationsLoading :boolean = false;

  paginationConfig = PaginationConfig;
  pagination :IPagination = {
    current_page: 1,
    from: null,
    last_page: 1,
    path: '',
    per_page: PaginationConfig.perPage,
    to: null,
    total: 0,
  };
  queryParams :{ [param :string] :string | string[] };

  trackById = trackById;

  private onDestroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data :{ appIntegrationAccount :IAppIntegrationAccount },
    private appIntegrationAccountLocationApiService :AppIntegrationAccountLocationApiService,
    private dialogRef :MatDialogRef<AppIntegrationAccountLocationComponent>,
    private snackBar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.initQueryParams();
    this.getInventoryLocations();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onPaginationEvent(pagination :PageEvent) :void {
    this.queryParams = {
      ...this.queryParams,
      limit: pagination.pageSize.toString(),
      page: (pagination.pageIndex + 1).toString(),
    };
    this.getInventoryLocations();
  }

  onClose() :void {
    this.dialogRef.close();
  }

  onSetForAllProducts(location :IAppIntegrationLocation) :void {
    this.loading = true;
    this.appIntegrationAccountLocationApiService
      .bulkUpdateLocation(this.data.appIntegrationAccount.id, location.id, {})
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (response :any) => {
          this.snackBar.open(
            response?.success || 'Successfully updated.',
            'Success',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
          this.getInventoryLocations();
        },
        error: (response) => {
          this.loading = false;
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  onSetDefaultLocation(location :IAppIntegrationLocation) :void {
    this.loading = true;
    this.appIntegrationAccountLocationApiService
      .setDefaultLocation(this.data.appIntegrationAccount.id, location.id)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (response :any) => {
          this.snackBar.open(
            response?.success || 'Successfully updated.',
            'Success',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
          this.getInventoryLocations();
        },
        error: (response) => {
          this.loading = false;
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  onGetLocations() :void {
    this.loading = true;
    this.btnGetLocationsLoading = true;
    this.appIntegrationAccountLocationApiService
      .syncLocations({
        appIntegrationAccountId: (
          this.data.appIntegrationAccount.id || 0
        ).toString(),
      })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.loading = false;
          this.btnGetLocationsLoading = false;
        }),
      )
      .subscribe({
        next: (response :any) => {
          this.inventoryLocations = response?.data;
          this.pagination = response?.meta;
        },
        error: (response) => {
          this.loading = false;
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private initQueryParams() :void {
    this.queryParams = {
      limit: PaginationConfig.perPage.toString(),
      page: PaginationConfig.page.toString(),
      sortDirection: 'desc',
      sortField: 'created_at',
    };
  }

  private getInventoryLocations() :void {
    this.loading = true;

    this.appIntegrationAccountLocationApiService
      .get({
        ...this.queryParams,
        appIntegrationAccountId: (
          this.data.appIntegrationAccount.id || 0
        ).toString(),
      })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (response :any) => {
          this.inventoryLocations = response?.data;
          this.pagination = response?.meta;
        },
        error: (response) => {
          this.loading = false;
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  get appIntegrationAccountHeadingTitle() :string {
    return `${this.data.appIntegrationAccount?.label || ''} Locations`;
  }
}
